import { IConfigData } from '@df/catalog/config/config.interfaces';
import { App } from '@df/core/app';

declare global {
    interface Window {
        adobe?: {
            target?: Ab.IAdobeTargetApi;
        };
        convert?: Ab.Convert.Instance;
    }
}

export namespace Ab {
    export namespace Convert {
        export interface Instance {
            readonly currentData: CurrentData;
            readonly data: Data;
            readonly isRedirect: boolean;
            readonly version?: string;
        }

        interface Data {
            readonly experiences?: Experience[];
            readonly experiments?: Record<string, Experiment>;
            readonly goals: Goal[];
            readonly locations: Location[];
        }
        interface CurrentData {
            readonly experiences?: Record<string, CurrentExperience>;
            readonly experiencesGoals?: Record<string, unknown>;
            readonly experiments?: Record<
                string,
                {
                    variation_id: string;
                }
            >;
            readonly experiments_goals?: Record<string, unknown>;
            readonly goals?: Record<string, unknown>;
        }

        interface CurrentExperience {
            readonly firstTime?: boolean;
            readonly variation?: Variation;
        }

        interface Variation {
            readonly id: string;
            readonly key: string;
            readonly name: string;
            readonly status: string;
        }

        interface Experience {
            readonly id: string;
            readonly name: string;
            readonly type: string;
            readonly status: string;
            readonly settings: {
                readonly min_order_value?: number;
                readonly max_order_value?: number;
                readonly matching_options?: { readonly audiences?: string; readonly locations?: string; readonly [key: string]: unknown };
                readonly outliers?: Record<string, { readonly detection_type?: string; readonly [key: string]: unknown }>;
            };
            readonly key: string;
            readonly version: number;
            readonly variations: Variation[];
        }

        interface Goal {
            readonly id: string;
            readonly name: string;
            readonly key: string;
            readonly type: string;
        }

        interface Location {
            readonly id: string;
            readonly key: string;
            readonly name: string;
            readonly rules: Record<string, unknown>;
            readonly trigger: Record<string, unknown>;
        }

        interface Experiment {
            readonly id: string;
            readonly vars: Record<string, Variation>;
            readonly vars_sort: number[];
        }
    }

    export interface IOptions {
        convertcom?: {
            script: (app: App) => string;
            trackRevenue?: boolean;
        };
        googleOptimize?: {
            containerId: (app: App) => string;
        };
        adobeTarget?: {
            enabled: boolean;
        };
    }

    export interface IExperimentStatus {
        experimentId: string;
        experimentName?: string;
        variantId?: string;
        variants: Record<string, { id: string; variant: Ab.TVariant; name: string; index: number }>;
    }

    export interface IExperimentDef {
        name: string;
        key: string;
        backend?: boolean;
        engine?: TEngine;
        adobetarget_parameters?: IExperimentDefAdobetargetParameter[];
        adobetarget_variants?: IExperimentDefAdobetargetVariant[];
    }

    export interface IExperimentDefAdobetargetParameter {
        parameter_name: string;
        parameter_value: string;
    }

    export interface IExperimentDefAdobetargetVariant {
        variant_name: string;
    }

    export type TEngine = 'convertcom' | 'adobetarget';

    export type TVariant = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

    /**
     * Config entry id
     */
    export const configId = 'ab.setup';

    /**
     * Config model data
     */
    export interface IModelData extends IConfigData<Ab.IConfigContent> {}

    export interface IConfigContent {
        experiments: IExperimentDef[];
    }

    export interface IExperimentState {
        // selected variant
        variant: TVariant;

        // if variant state is initial or it was set from google
        isInitial: boolean;
        // if experiment is missing in 3rdparty data
        missing?: boolean;
    }

    export interface IExperimentStatusInfo {
        name: string;
        key: string;
        variant: Ab.TVariant | undefined;
        active: boolean;
        variants: Ab.TVariant[];
    }

    export interface IAdobeTargetApi {
        getOffers(data: IAdobeTargetGetOffersData): Promise<IAdobeTargetGetOffersResponse>;
    }

    export interface IAdobeTargetGetOffersData {
        consumerId?: string;
        decisioningMethod?: string;
        request: {
            execute: {
                pageLoad?: {
                    parameters?: { [key: string]: string };
                    profileParameters?: { [key: string]: string };
                };
            };
        };
        timeout?: number;
    }

    export interface IAdobeTargetGetOffersResponse {
        meta?: { decisioningMethod?: string };
        execute?: {
            pageLoad?: {
                options?: IAdobeTargetGetOffersResponseDetails[];
            };
        };
    }

    export interface IAdobeTargetGetOffersResponseDetails {
        type?: 'html' | 'json' | 'redirect' | 'dynamic' | 'actions';
        responseTokens?: IAdobeTargetGetOffersResponseDetailsResponseTokens;
    }

    export interface IAdobeTargetGetOffersResponseDetailsResponseTokens {
        'activity.id'?: string;
        'activity.name'?: string;
        'experience.name'?: string;
        'experience.id'?: string;
        'offer.id'?: string;
        'offer.name'?: string;
        'option.id'?: string;
        'option.name'?: string;
    }
}
